import { HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';

import { ApiError, IAPIErrorHandlingOptions, safeCause } from '@sondermind/utilities/models-http';

/**
 * Pull `base` out of a server `errors` object and keep the remaining
 * field-level errors as `fields`. Promoting `base` lets Error.message
 * formatting and UI consumers rely on a typed shape; `fields` keeps the
 * rest isolated under a single property instead of being spread.
 */
function extractBaseAndFields(errors: Record<string, unknown>): {
  base?: string[];
  fields?: Record<string, unknown>;
} {
  const rawBase = errors['base'];
  const base = Array.isArray(rawBase) ? rawBase.map((m) => String(m)) : undefined;
  const { base: _omit, ...rest } = errors;
  const fields = Object.keys(rest).length > 0 ? rest : undefined;
  return { base, fields };
}

export function defaultHandleHttpError(
  err: HttpErrorResponse,
  options: IAPIErrorHandlingOptions
): Observable<never> {
  // Check if running in the browser
  const isBrowser = typeof window !== 'undefined';

  // server couldn't respond at all - network errors, bug in httpclient, etc.
  if (isBrowser && err.error instanceof ErrorEvent) {
    return throwError(() => new ApiError({
      status: 0,
      base: ['Something didn\'t work, please try again.'],
      serverMessage: err.message,
      url: err.url,
      cause: safeCause(err)
    }));
  }

  // server responded, but it may be coming from proxies, etc, so it might not be in the right format
  if (typeof err.error === 'string') {
    return throwError(() => new ApiError({
      status: err.status,
      base: [err.error],
      serverMessage: err.error,
      url: err.url,
      cause: safeCause(err)
    }));
  }

  // server responded and it looks nominally like it's in our format
  if (err.error !== null && typeof err.error === 'object' && err.error.errors) {
    const { base, fields: nestedFields } = extractBaseAndFields(err.error.errors);
    // rawErrors callers want the whole envelope (e.g. for top-level `code` fields
    // alongside the `errors` map); non-raw callers only get the field-level errors.
    const fields = options.rawErrors
      ? { ...err.error, errors: nestedFields }
      : nestedFields;
    return throwError(() => new ApiError({
      status: err.status,
      base,
      fields,
      serverMessage: err.message,
      url: err.url,
      cause: safeCause(err)
    }));
  }

  // server responded with an unrecognized envelope (no `errors` key). If the
  // body has a top-level `message`, surface it as `base` — load-bearing for
  // consumers like quick-checkout that branch on `error.message.includes(...)`
  // — but keep `serverMessage` as Angular's formatted string so Sentry still
  // groups by endpoint and we don't widen the PHI surface unintentionally.
  const bodyMessage = typeof err.error === 'object' && err.error !== null
    ? (err.error as { message?: unknown }).message
    : undefined;
  const base = typeof bodyMessage === 'string' && bodyMessage.length > 0
    ? [bodyMessage]
    : ['Some request didn\'t work, please try again.'];
  return throwError(() => new ApiError({
    status: err.status,
    base,
    serverMessage: err.message ?? 'Unknown error',
    url: err.url,
    cause: safeCause(err)
  }));
}
